import React, { useRef, useState, useEffect } from 'react'

import { FilePond as FilePondOriginal, FilePondProps } from 'react-filepond'
import * as fp from 'filepond'

// Type assertion to fix React types incompatibility between react-filepond and react-intl
const FilePond: React.FC<FilePondProps & { ref?: React.Ref<FilePondOriginal> }> =
  FilePondOriginal as any

import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import { isNil } from 'ramda'
import { useDebounce } from 'react-use'

import Papa from 'papaparse'
import xlsx from 'xlsx/dist/xlsx.core.min.js'

import { FormattedMessage } from 'react-intl'
import intl from 'localization/components'
import { useThemeMode } from 'contexts/theme-context'

import './styles/filepond.css'
fp.registerPlugin(FilePondPluginFileValidateType)
import 'filepond/dist/filepond.min.css'

const acceptedFileTypes = [
  'text/csv', // Mac CSV
  'application/csv', // Mac CSV
  'application/vnd.ms-excel', // Windows CSV
  'text/comma-separated-values, application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

const isFileCSV = (file: File): boolean =>
  file.name.split('.').pop()?.toLowerCase() === 'csv'

const convertExcelToCSV = (str: string): string => {
  const workbook = xlsx.read(str, { type: 'binary' })
  const sheetName = workbook.SheetNames[0]

  return xlsx.utils.sheet_to_csv(workbook.Sheets[sheetName])
}

interface ImportDropzoneProps {
  onFileLoaded: (rows: Record<string, string>[] | null) => void
}

const ImportDropzone = ({ onFileLoaded }: ImportDropzoneProps) => {
  const { resolvedMode } = useThemeMode()
  const filePondRef = useRef<FilePondOriginal | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [debouncedFiles, setDebouncedFiles] = useState<File[]>([])

  useDebounce(
    () => {
      setFiles(debouncedFiles)
    },
    300,
    [debouncedFiles]
  )

  const handleSetFiles = (fileItems: { file: File }[]) => {
    const [fileItem] = fileItems
    if (fileItem?.file) {
      setDebouncedFiles([fileItem.file])
    }
  }

  useEffect(() => {
    const [file] = files

    if (!isNil(file) && acceptedFileTypes?.includes(file.type)) {
      const fileReader = new FileReader()
      fileReader.readAsBinaryString(file)

      fileReader.onloadend = () => {
        const { result } = fileReader

        if (result == null || result instanceof ArrayBuffer) {
          onFileLoaded(null)
          return
        }

        const csv = isFileCSV(file) ? result : convertExcelToCSV(result)

        const results = Papa.parse(csv, { header: true })
        onFileLoaded(results.data as Record<string, string>[])
      }
    } else {
      onFileLoaded(null)
    }
  }, [files, onFileLoaded])

  return (
    <FormattedMessage id={intl.riskMonitoringNew('drag-drop')}>
      {(label) => (
        <div style={{ cursor: 'pointer' }} data-theme={resolvedMode}>
          <FilePond
            ref={filePondRef}
            files={files as unknown as string[]}
            allowMultiple={false}
            labelIdle={label as unknown as string}
            acceptedFileTypes={acceptedFileTypes}
            onupdatefiles={handleSetFiles as unknown as (files: unknown[]) => void}
          />
        </div>
      )}
    </FormattedMessage>
  )
}

export default ImportDropzone
